import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Toast {
  id: number;
  message: string;
  /** optional action button (e.g. "View cart", "Undo") */
  actionLabel?: string;
  action?: () => void;
  /** ms before it hides itself; 0 = sticky until dismissed */
  duration: number;
  tone: 'default' | 'success' | 'error';
}

/**
 * Small bottom toasts ("Added to cart - View cart", "Removed - Undo").
 * Rendered once by <app-toast> in the landing layout; any service or page can
 * show one.
 */
@Injectable( {
  providedIn: 'root',
} )
export class ToastService {

  private nextId = 1;
  private readonly toastsSubject = new BehaviorSubject<Toast[]>( [] );
  readonly toasts$: Observable<Toast[]> = this.toastsSubject.asObservable();

  show( message: string, options: Partial<Omit<Toast, 'id' | 'message'>> = {} ): number {
    const toast: Toast = {
      id: this.nextId++,
      message,
      actionLabel: options.actionLabel,
      action: options.action,
      duration: options.duration ?? 4500,
      tone: options.tone ?? 'default',
    };
    // one toast at a time keeps the corner calm; a new one replaces the old
    this.toastsSubject.next( [ toast ] );
    if ( toast.duration > 0 ) {
      setTimeout( () => this.dismiss( toast.id ), toast.duration );
    }
    return toast.id;
  }

  dismiss( id: number ): void {
    this.toastsSubject.next( this.toastsSubject.value.filter( t => t.id !== id ) );
  }

  /** runs the toast's action, then hides it. */
  act( toast: Toast ): void {
    try {
      toast.action?.();
    } finally {
      this.dismiss( toast.id );
    }
  }
}
